import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock,
  Infinity as InfinityIcon,
  ListTodo,
  Loader2,
  TrendingUp,
} from 'lucide-react';

const BUCKET_NOUN = { week: 'day', month: 'week', quarter: 'month', year: 'month' };

const formatHours = (value) => `${Number(value || 0).toFixed(1)}h`;

/**
 * Consolidated monthly / quarterly / yearly team view built from
 * GET /progress/summary.
 */
const ConsolidatedTeamSummary = ({ summary, loading }) => {

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!summary) {
    return (
      <Card className="border-border">
        <CardContent className="p-12 text-center text-sm text-muted-foreground">
          Could not load the consolidated summary.
        </CardContent>
      </Card>
    );
  }

  const t = summary.totals;
  const maxBucketHours = Math.max(...summary.buckets.map(b => b.hours), 1);

  const stats = [
    { label: 'Team Hours', value: t.total_hours.toFixed(1), icon: Clock },
    { label: 'Hrs/Person', value: t.avg_hours_per_person.toFixed(1), icon: TrendingUp },
    { label: 'Completed', value: t.tasks_completed, icon: CheckCircle2, className: 'text-green-500' },
    { label: 'Completion', value: `${t.completion_rate}%`, icon: BarChart3 },
    { label: 'Open Tasks', value: t.tasks_pending, icon: ListTodo },
    { label: 'Ongoing', value: t.tasks_ongoing, icon: InfinityIcon },
  ];

  return (
    <div className="space-y-6" data-testid={`consolidated-summary-${summary.period}`}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        {stats.map(stat => (
          <Card key={stat.label} className="border-border bg-muted/30">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="h-10 w-10 bg-primary/10 hidden sm:flex items-center justify-center shrink-0">
                <stat.icon className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className={`text-xl sm:text-2xl font-bold ${stat.className || ''}`}>{stat.value}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            HOURS BY {BUCKET_NOUN[summary.period].toUpperCase()}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {summary.buckets.map(bucket => (
            <div key={bucket.key} className="grid grid-cols-[minmax(0,9rem)_1fr_3.5rem] sm:grid-cols-[14rem_1fr_4rem] items-center gap-3 text-xs">
              <span className="truncate text-muted-foreground" title={bucket.label}>{bucket.label}</span>
              <div className="h-2.5 bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${(bucket.hours / maxBucketHours) * 100}%` }} />
              </div>
              <span className="text-right font-mono">{formatHours(bucket.hours)}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            TEAM PROGRESS
          </CardTitle>
        </CardHeader>
        <CardContent>
          {summary.members.length > 0 ? (
            <div className="overflow-x-auto -mx-4 md:mx-0">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="table-header !text-left pl-2 md:pl-4">MEMBER</th>
                    <th className="table-header !text-center">HOURS</th>
                    <th className="table-header !text-center">DAYS</th>
                    <th className="table-header !text-center">MISSED</th>
                    <th className="table-header !text-center">DONE</th>
                    <th className="table-header !text-center">OPEN</th>
                    <th className="table-header !text-center">ONGOING</th>
                    <th className="table-header !text-center">RATE</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.members.map(member => (
                    <tr key={member.user_id} className="table-row" data-testid={`summary-member-${member.user_id}`}>
                      <td className="py-2 pl-2 pr-2 md:py-3 md:pl-4">
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                            {member.user_name.charAt(0).toUpperCase()}
                          </div>
                          <span className="truncate">{member.user_name}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-center font-mono">{formatHours(member.total_hours)}</td>
                      <td className="py-2 px-2 text-center">{member.days_logged}/{member.working_days}</td>
                      <td className="py-2 px-2 text-center">
                        {member.missed_days > 0 ? (
                          <span className="inline-flex items-center gap-1 text-orange-500 font-semibold">
                            <AlertTriangle className="h-3 w-3" />
                            {member.missed_days}
                          </span>
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-green-500 inline" />
                        )}
                      </td>
                      <td className="py-2 px-2 text-center"><Badge variant="success" className="text-xs">{member.tasks_completed}</Badge></td>
                      <td className="py-2 px-2 text-center"><Badge variant="outline" className="text-xs">{member.tasks_pending}</Badge></td>
                      <td className="py-2 px-2 text-center">{member.tasks_ongoing}</td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 bg-muted overflow-hidden flex-1">
                            <div className="h-full bg-green-500" style={{ width: `${member.completion_rate}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground w-9 text-right">{member.completion_rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No active team members</p>
          )}
        </CardContent>
      </Card>

      {summary.top_tasks.length > 0 && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <ListTodo className="h-4 w-4" />
              TOP TASKS BY HOURS
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {summary.top_tasks.map(item => (
              <div key={item.task_id || 'none'} className="flex items-center justify-between gap-3 p-3 border border-border">
                <span className="text-sm truncate">{item.title}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {item.entries} entr{item.entries === 1 ? 'y' : 'ies'} · <span className="font-mono font-medium text-foreground">{formatHours(item.hours)}</span>
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <p className="text-[10px] text-muted-foreground">
        Ongoing tasks are excluded from completion rates. Missed days count elapsed weekdays with no timesheet entry.
      </p>
    </div>
  );
};

export default ConsolidatedTeamSummary;
